import { UnifiedSearchResultModel, SearchProductWithSkusModel } from '@bizzkit/ecommerce-search/client';
import { QueryClient, useQuery, UseQueryResult } from '@tanstack/react-query';
import { FetchProductBandParams, fetchProductBand } from '@/api/product/bands';
import { ProductListPageQuery, searchProducts } from '@/api/product/search';
import { Filter, FilterType, ProductSort, SortDirection } from '@/api/product/types';
import { ProblemDetails, SearchSettingsResponse, SegmentationId } from '@/generated/data.schemas';
import { DynamicProductQuery } from '@/helpers';
import {
	DynamicProductSelector,
	FilterModel as DynamicFilterModel,
	FilterType as DynamicFilterType,
} from '@/types/dynamicProducts';
import { queryKeys } from '../apiConfig';

export const usePrecacheProductsQuery = (
	ids: string[],
	isActive: boolean,
	settings?: SearchSettingsResponse,
): UseQueryResult<UnifiedSearchResultModel | null | undefined, undefined | ProblemDetails> => {
	const params: FetchProductBandParams = {
		searchApiHost: settings?.searchApiHost ?? '',
		bandsScopeId: settings?.bandsScopeId ?? '',
		searchSegment: settings?.searchSegment ?? '',
		searchToken: settings?.searchToken ?? '',
		productIds: ids,
	};

	return useQuery({
		queryKey: queryKeys.product.band(params).queryKey,
		queryFn: () => fetchProductBand(params),
		enabled: !!settings && ids.length > 0 && isActive,
	});
};

export const preCacheProductBlocks = (
	queryClient: QueryClient,
	products: UnifiedSearchResultModel | null | undefined,
	subSets: string[][],
	settings?: SearchSettingsResponse,
): void => {
	if (!settings?.searchToken || settings.searchToken.length === 0) return;

	if (!products?.totalProducts || !subSets.length || !settings) {
		return;
	}

	for (const subSet of subSets) {
		const subSetProducts: SearchProductWithSkusModel[] = [];
		for (const productId of subSet) {
			const product = products.products?.find((x) => x.id === productId);
			if (product) {
				subSetProducts.push(product);
			}
		}
		const result: UnifiedSearchResultModel = {
			products: subSetProducts,
		};

		const params: FetchProductBandParams = {
			searchApiHost: settings?.searchApiHost ?? '',
			bandsScopeId: settings?.bandsScopeId ?? '',
			searchSegment: settings?.searchSegment ?? '',
			searchToken: settings?.searchToken ?? '',
			productIds: subSet,
		};

		const cacheKey = queryKeys.product.band(params).queryKey;
		queryClient.setQueryData(cacheKey, result);
	}
};

const DefaultSortBy = ProductSort.Score;
const DefaultSortDirection = SortDirection.Descending;
export const DefaultPageSize = 6;

// Map filters received from dynamic products plugin to filters used in product search query
const mapFilters = (filters: readonly DynamicFilterModel[]): Filter[] => {
	return filters.map((f) => {
		if (f.type === DynamicFilterType.TermFilter) {
			return {
				id: f.key,
				type: FilterType.String,
				values: f.values.map((v) => ({ label: v, value: v, isSelected: true })),
			};
		} else {
			return {
				id: f.key,
				type: FilterType.Number,
				min: f.from,
				max: f.to,
				selectedMin: f.from,
				selectedMax: f.to,
			};
		}
	});
};

export const parseDynamicProductSearchQuery = (
	segmentationId: SegmentationId,
	params?: DynamicProductSelector,
	pageSize?: number,
): ProductListPageQuery => {
	const [price] =
		params?.filters.flatMap((filter) => {
			return filter.key === 'Price' && filter.type === DynamicFilterType.RangeFilter ? [filter] : [];
		}) ?? [];

	return {
		segmentationId: segmentationId,
		phrase: params?.phrase ?? undefined,
		pageSize: pageSize || DefaultPageSize,
		sortBy: DefaultSortBy,
		sortDirection: DefaultSortDirection,
		filters: mapFilters(params?.filters ?? []),
		minPrice: price?.from,
		maxPrice: price?.to,
	};
};

export const preCacheDynamicProductQuery = async (params: {
	queryClient: QueryClient;
	queries: readonly DynamicProductQuery[];
	segmentationId: SegmentationId;
	settings?: SearchSettingsResponse;
}): Promise<void> => {
	const requests = params.queries.map((queryInfo) => {
		const searchQuery = parseDynamicProductSearchQuery(params.segmentationId, queryInfo.query, queryInfo.count);
		return params.queryClient.prefetchQuery({
			queryKey: queryKeys.product.dynamicSearch(searchQuery, params.settings).queryKey,
			queryFn: () => searchProducts(searchQuery, 1, params.settings, true),
		});
	});

	await Promise.allSettled(requests);
};

export const useDynamicProductSearchQuery = (
	query: ProductListPageQuery,
	settings?: SearchSettingsResponse,
): UseQueryResult<UnifiedSearchResultModel, undefined | ProblemDetails> => {
	return useQuery({
		queryKey: queryKeys.product.dynamicSearch(query, settings).queryKey,
		queryFn: () => {
			return searchProducts(query, 1, settings, true);
		},
	});
};
